import {
    Box,
    Checkbox,
    FormControlLabel,
    TablePagination,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import { ApplicationProps } from "../../redux-store/types/api-types";
import {
    Dispatch,
    SetStateAction,
    useEffect,
    useState
} from "react";
import { getAllApplications } from "../../redux-store/actions/application";
import { GetAllApplicantionsResponseBody } from "../../redux-store/types/application";
import {
    useDispatch,
    useSelector
} from "react-redux";
import {
    checkIfApplicationIsNonIndividual, getFirstHolderName
} from "../../utils/utilityFunctions";
import {
    applicantStatusMasters, APPLICATION_LISTING_STATUS, APPLICATION_TYPE, ApplicationStatusBasedOnRole, IndividualApplicationStatusBasedOnRole, MPOWER_ACCOUNT_TYPE_MASTER, USER_ROLES
} from "../../utils/constant";
import {
    ColumnType,
    DataTable
} from "../DataTable";
import { RootStateType } from "../../redux-store/reducers";

const getTableHeader = (role: string, application: ApplicationProps): ColumnType[] => {
    return [
        {
            header: 'Investor Name',
            valueGetter: ({ applicants = [] }: ApplicationProps) => (
                <Typography variant='subtitle_500'>
                    {getFirstHolderName(applicants)}
                </Typography>
            ),
        },
        {
            header: 'Application ID',
            valueGetter: (row: ApplicationProps) => (
                <Typography variant='subtitle_500'>
                    {row?.applicationNumber ?? 'N/A'}
                </Typography>
            ),
        },
        {
            header: 'Investor PAN',
            valueGetter: ({ applicants = [] }: ApplicationProps) => (
                <Typography variant='subtitle_500'>
                    {applicants[0]?.panNo || 'N/A'}
                </Typography>
            ),
        },
        {
            header: 'Mode of Holding / Status',
            renderCell: (row: ApplicationProps) => {
                return (
                    <Typography
                        variant='subtitle_500'
                        sx={{ textTransform: 'capitalize', }}
                    >
                        {row.modeOfHolding || 'Single'} / {
                            row?.applicants?.[0]?.status ?
                                applicantStatusMasters[row.applicants[0].status] ?
                                    applicantStatusMasters[row.applicants[0].status] : row.applicants[0].status
                                : "N/A"
                        }
                    </Typography>
                );
            },
        },
        {
            header: 'Account Type',
            valueGetter: (row: ApplicationProps) => (
                <Typography variant='subtitle_500'>
                    {MPOWER_ACCOUNT_TYPE_MASTER[row?.accountType] ?? 'N/A'}
                </Typography>
            ),
        },
        ...([APPLICATION_TYPE.INDIVIDUAL].includes(`${application?.applicationType ?? ''}`.toLowerCase() as APPLICATION_TYPE)) ? [
            {
                header: 'Joint Holder 1 Name',
                valueGetter: ({ applicants = [] }: ApplicationProps) => (
                    <Typography variant='subtitle_500'>
                        {applicants[1]?.name || 'N/A'}
                    </Typography>
                ),
            },
            {
                header: 'Joint Holder 1 PAN',
                valueGetter: ({ applicants = [] }: ApplicationProps) => (
                    <Typography variant='subtitle_500'>
                        {applicants[1]?.panNo || 'N/A'}
                    </Typography>
                ),
            },
            {
                header: 'Joint Holder 2 Name',
                valueGetter: ({ applicants = [] }: ApplicationProps) => (
                    <Typography variant='subtitle_500'>
                        {applicants[2]?.name || 'N/A'}
                    </Typography>
                ),
            },
            {
                header: 'Joint Holder 2 PAN',
                valueGetter: ({ applicants = [] }: ApplicationProps) => (
                    <Typography variant='subtitle_500'>
                        {applicants[2]?.panNo || 'N/A'}
                    </Typography>
                ),
            },
        ] : [],
        {
            header: 'Checker Status',
            renderCell: (row: ApplicationProps) => {
                const applicationStatus = row.hasPOA
                    ? ApplicationStatusBasedOnRole[role][row.status]
                    : checkIfApplicationIsNonIndividual(row)
                        ? ApplicationStatusBasedOnRole[role][row.status]
                        : !checkIfApplicationIsNonIndividual(row) && role === USER_ROLES.AMCAPPROVER
                            ? IndividualApplicationStatusBasedOnRole[role][row.status]
                            : APPLICATION_LISTING_STATUS[row.status];
                return (
                    <Typography
                        className={applicationStatus}
                        sx={(theme: Theme) => ({
                            textDecoration: 'none',
                            ...theme.typography.subtitle_500,
                            color: '#B78813',
                            '&.Draft': {
                                color: 'rgba(51, 127, 201, 0.7)',
                            },
                            '&.Completed': {
                                fontWeight: 600,
                                color: 'rgba(35, 133, 63, 0.7)',
                            },
                            '&.Rejected': {
                                color: '#BD2932 !important',
                                fontWeight: 500,
                            },
                        })}
                    >
                        {applicationStatus}
                    </Typography>
                );
            },
        }
    ];
}

export default function DuplicatePanApplications({
    application,
    setDuplicatePanApplicationCount,
    setDuplicatePanCheckboxValue,
    duplicatePanCheckboxValue
}: {
    application: ApplicationProps;
    setDuplicatePanApplicationCount: Dispatch<SetStateAction<number>>;
    setDuplicatePanCheckboxValue: Dispatch<SetStateAction<boolean>>;
    duplicatePanCheckboxValue: boolean;
}): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch()

    const { role = '' } = useSelector((store: RootStateType) => store.auth);

    const [isTableDataLoading, setTableDataLoading] = useState<boolean>(false)
    const [tableData, setTableData] = useState<Array<ApplicationProps>>([])
    const [tableDataTotalCount, setTableDataTotalCount] = useState<number>(0)
    const [tableDataLimit, setTableDataLimit] = useState<number>(10)
    const [tableCurrentPage, setTableCurrentPage] = useState<number>(1)

    useEffect(() => {
        let componentIsAlive = true;

        setTableDataLoading(true);
        setTableData([]);

        (async function () {
            try {
                if (application?.applicants?.[0]?.panNo) {
                    const response = (await dispatch(
                        getAllApplications({
                            page: tableCurrentPage,
                            search: application?.applicants?.[0]?.panNo,
                            storeInRedux: false
                        })
                    )) as unknown as GetAllApplicantionsResponseBody;

                    const { applications = [], total = 0, limit = 0 } = response || {};
                    if (!componentIsAlive) {
                        return;
                    }

                    setDuplicatePanApplicationCount((applications ?? []).length)
                    setTableData(applications);
                    setTableDataTotalCount(total)
                    setTableDataLimit(limit);
                }
            } catch (e) {
                console.error((e as Error).message);
            } finally {
                setTableDataLoading(false);
            }
        })();

        return () => {
            componentIsAlive = false;
        };
    }, [tableCurrentPage, application]);

    if (tableData.length === 1) {
        return <></>
    }

    return (
        <Box
            sx={(theme: Theme) => ({
                bgcolor: theme.palette.common.white,
                borderRadius: '10px',
                border: `1px solid ${theme.palette.grey[100]}`,
                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}1A`,
                padding: '25px',
            })}
            mt={5}
        >
            <Typography variant='body_700' sx={{ color: 'primary.900' }}>
                All Applications with same Primary PAN
            </Typography>
            <Box mt={2}>
                <DataTable
                    isLoading={isTableDataLoading}
                    tableHeader={[...getTableHeader(role, application)]}
                    tableData={tableData}
                    rowCustomStyles={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '.MuiTableCell-body ': { color: theme.palette.text.primary, fontWeight: 500 },
                        background: 'transparent',
                    }}
                    boxStyles={{ minWidth: 100 }}
                />
                <TablePagination
                    sx={{
                        color: theme.palette.text.primary[500],
                        padding: '20px 35px 35px 35px',
                        ...theme.typography.body_500,
                    }}
                    rowsPerPageOptions={[]}
                    labelRowsPerPage=""
                    component="div"
                    count={tableDataTotalCount}
                    rowsPerPage={tableDataLimit}
                    page={tableCurrentPage - 1}
                    onPageChange={(event, num) => setTableCurrentPage(num + 1)}
                />
            </Box>
            <Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={duplicatePanCheckboxValue}
                            onChange={(event, checked) => setDuplicatePanCheckboxValue(checked)}
                        />
                    }
                    label={
                        <Typography
                            variant="body_500"
                            color={(USER_ROLES.AMCAPPROVER === role && application?.status === 'sent_to_amc_approver') ? 'text.primary' : 'text.secondary'}
                        >
                            The above Applications are also available with same Primary PAN. Do you still want to continue?
                        </Typography>
                    }
                    disabled={!(USER_ROLES.AMCAPPROVER === role && application?.status === 'sent_to_amc_approver')}
                />
            </Box>
        </Box>
    )
}